import React from 'react';
import V2Icon from './V2Icon';

export type V2SearchOption = {
  id: number;
  label: string;
  subtitle?: string;
  href: string;
};

type V2PlayerSearchProps = {
  inputId?: string;
  variant?: 'header' | 'hero' | 'compact';
  autoFocus?: boolean;
  query?: string;
  onQueryChange?: (value: string) => void;
  onSubmit?: () => void;
  options?: V2SearchOption[];
  onSelectOption?: (id: number) => void;
  emptyNote?: string;
  onFocus?: () => void;
  onBlur?: () => void;
};

function shouldUseNativeLink(event: React.MouseEvent<HTMLAnchorElement>) {
  return event.button !== 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
}

const V2PlayerSearch: React.FC<V2PlayerSearchProps> = ({
  inputId = 'search-player',
  variant = 'header',
  autoFocus = false,
  query = '',
  onQueryChange,
  onSubmit,
  options = [],
  onSelectOption,
  emptyNote = 'Игрок не найден.',
  onFocus,
  onBlur,
}) => {
  const [open, setOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const optionRefs = React.useRef<Array<HTMLAnchorElement | null>>([]);

  const selectOption = React.useCallback(
    (option: V2SearchOption) => {
      onSelectOption?.(option.id);
      setOpen(false);
      setActiveIndex(-1);
    },
    [onSelectOption],
  );

  React.useEffect(() => {
    setActiveIndex(-1);
  }, [options, query]);

  React.useEffect(() => {
    if (activeIndex < 0) {
      return;
    }

    optionRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      if (!options.length) {
        return;
      }

      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => (index < options.length - 1 ? index + 1 : 0));
      return;
    }

    if (event.key === 'ArrowUp') {
      if (!options.length) {
        return;
      }

      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => (index > 0 ? index - 1 : options.length - 1));
      return;
    }

    if (event.key === 'Enter' && open && activeIndex >= 0 && options[activeIndex]) {
      event.preventDefault();
      selectOption(options[activeIndex]);
      return;
    }

    if (event.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <form
      className={variant === 'hero' ? 'player-search player-search--hero' : 'player-search'}
      role="search"
      onSubmit={(event) => {
        event.preventDefault();
        setOpen(Boolean(query.trim()));
        onSubmit?.();
      }}
    >
      <label className="sr-only" htmlFor={inputId}>
        {variant === 'hero' ? 'Введите ник игрока' : 'Найти игрока'}
      </label>
      <span>
        <V2Icon name="search" />
      </span>
      <input
        id={inputId}
        placeholder={variant === 'hero' ? 'Введите ник игрока' : 'Найти'}
        autoComplete="off"
        autoFocus={autoFocus}
        value={query}
        role="combobox"
        aria-expanded={open}
        aria-controls={`${inputId}-listbox`}
        aria-activedescendant={
          activeIndex >= 0 ? `${inputId}-option-${options[activeIndex]?.id}` : undefined
        }
        onChange={(event) => {
          onQueryChange?.(event.target.value);
          setOpen(Boolean(event.target.value.trim()));
        }}
        onKeyDown={handleInputKeyDown}
        onFocus={() => {
          onFocus?.();
          setOpen(Boolean(query.trim()));
        }}
        onBlur={onBlur}
      />
      {variant === 'header' ? (
        <>
          <kbd>/</kbd>
          <button type="submit" aria-label="Искать игрока">
            ↵
          </button>
        </>
      ) : variant === 'compact' ? null : (
        <button type="submit" className="acid-button player-search-submit">
          Найти ↵
        </button>
      )}
      <div id={`${inputId}-listbox`} className="search-result" role="listbox" hidden={!open}>
        {options.map((option, index) => (
          <a
            key={option.id}
            id={`${inputId}-option-${option.id}`}
            ref={(element) => {
              optionRefs.current[index] = element;
            }}
            className={
              index === activeIndex
                ? 'search-result__option search-result__option--active'
                : 'search-result__option'
            }
            href={option.href}
            role="option"
            aria-selected={index === activeIndex}
            onMouseDown={(event) => {
              if (event.button === 0) {
                event.preventDefault();
              }
            }}
            onMouseEnter={() => setActiveIndex(index)}
            onAuxClick={(event) => event.stopPropagation()}
            onContextMenu={(event) => event.stopPropagation()}
            onClick={(event) => {
              if (shouldUseNativeLink(event)) {
                event.stopPropagation();
                return;
              }

              event.preventDefault();
              event.stopPropagation();
              selectOption(option);
            }}
          >
            <span>
              {option.label} {option.subtitle ? <small>{option.subtitle}</small> : null}
            </span>
            <span>↗</span>
          </a>
        ))}
        <p className="muted">{options.length ? '' : emptyNote}</p>
      </div>
    </form>
  );
};

export default V2PlayerSearch;
